import React, { useState, FC } from "react";
import getModalStyle from "../../../../util/getModalStyle";
import {
  Typography,
  Button,
  TextField,
  CircularProgress,
  Modal,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { UseMutationResult } from "react-query";
import { AdminOrderNumsState } from "../QuotesTable/useQuotesTableSelector";
import { StyledModalDiv } from "../../../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import { QuoteObj, UserObj, Overwrite } from "../../../../GlobalTypes";
import { generateOrderAckPDF } from "../../../../api/pdf";
import createDownloadLink from "../../../../util/createDownloadLink";

type AdminQuoteObj = Overwrite<QuoteObj, { userId: UserObj }>;

const StyledColDiv = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginBottom: 20,
});

const StyledDivRow = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  marginBottom: 20,
});

function getPlaceholder(name: string) {
  switch (name) {
    case "salesOrder":
      return "Enter Sales Order";
    case "invoiceNumber":
      return "Enter Invoice Number";
    case "depositDate":
      return "Enter Deposit Recieved";
    default:
      return "Enter Glass Ordered";
  }
}

interface AdminOrderModalProps {
  handleClose: () => void;
  modalText: string;
  open: boolean;
  mutation: UseMutationResult<void, unknown, void, unknown>;
  value: AdminOrderNumsState | {};
  name: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  soMatch: boolean;
  sendOrderAck: string;
  handleCheckOrderAck: (event: SelectChangeEvent<string>) => void;
  orderAckErr: boolean;
  selectedQuote?: AdminQuoteObj;
}

const AdminOrderModal: FC<AdminOrderModalProps> = ({
  handleClose,
  modalText,
  open,
  mutation,
  value,
  name,
  handleChange,
  soMatch,
  sendOrderAck,
  handleCheckOrderAck,
  orderAckErr,
  selectedQuote,
}) => {
  const [modalStyle] = useState(() => getModalStyle(50, 50));

  const [loadPdf, setLoadPdf] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      return mutation.mutate();
    }
  };

  const prevent = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event?.preventDefault();
    }
  };

  const handlePdf = async () => {
    if (!selectedQuote) return;
    setLoadPdf(true);
    try {
      const res = await generateOrderAckPDF(
        selectedQuote._id,
        "salesOrder" in value ? value.salesOrder : "",
      );
      createDownloadLink(res.data, `AGM_OrderAck.pdf`);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadPdf(false);
    }
  };

  const body = (
    <StyledModalDiv style={modalStyle} onKeyDown={handleKeyDown}>
      <h3 id="simple-modal-description" style={{ marginBottom: 40 }}>
        {modalText}
      </h3>

      <StyledColDiv>
        <StyledDivRow>
          {name === "invoiceNumber" && (
            <Typography style={{ paddingRight: 20 }}>
              Invoice Number:{" "}
            </Typography>
          )}

          <TextField
            label={getPlaceholder(name)}
            value={name in value ? value[name] : ""}
            type={
              name === "depositDate" || name === "glassOrderDate"
                ? "date"
                : "text"
            }
            name={name}
            onChange={handleChange}
            style={{ width: 180 }}
            size="small"
            variant="outlined"
            onKeyDown={prevent}
          />
        </StyledDivRow>

        {name === "salesOrder" && (
          <Typography style={{ color: "red", fontSize: "14px"}}>
            *Acceptable formats: 12345 or 12345A (no spaces)
          </Typography>
        )}

        {name === "invoiceNumber" && (
          <StyledDivRow>
            <Typography style={{ paddingRight: 20 }}>Invoice Date: </Typography>
            <TextField
              value={"invoiceDate" in value ? value.invoiceDate : ""}
              type="date"
              name={"invoiceDate"}
              onChange={handleChange}
              size="small"
              variant="outlined"
              onKeyDown={prevent}
            />
          </StyledDivRow>
        )}

        {soMatch && name === "salesOrder" && (
          <div style={{ display: "flex", padding: 10 }}>
            <Typography>
              Sales Order already exists do you want to add anyway?
            </Typography>
          </div>
        )}

        {name === "salesOrder" && (
          <StyledColDiv style={{ marginBottom: 10 }}>
            <FormControl style={{ padding: 10 }}>
              <FormControlLabel
                labelPlacement="start"
                control={
                  <Select
                    size="small"
                    style={{ marginLeft: 10 }}
                    value={sendOrderAck}
                    onChange={handleCheckOrderAck}
                  >
                    <MenuItem value={"no"}>No</MenuItem>
                    <MenuItem value={"yes"}>Yes</MenuItem>
                  </Select>
                }
                label="Send Order Acknowledgement:"
              />
            </FormControl>

            {selectedQuote?.userId.factor &&
            "salesOrder" in value &&
            value.salesOrder ? (
              <Button
                style={{ marginTop: 10, marginBottom: 10 }}
                variant="contained"
                color="customGrey"
                size="small"
                disabled={loadPdf}
                onClick={handlePdf}
              >
                {loadPdf ? "Generating..." : "Preview Acknowledgement"}
              </Button>
            ) : (
              <Button
                style={{ marginTop: 10, marginBottom: 10 }}
                variant="contained"
                color="customGrey"
                size="small"
                disabled={true}
              >
                Preview Acknowledgement
              </Button>
            )}
          </StyledColDiv>
        )}

        {name === "salesOrder" && orderAckErr && (
          <div style={{ display: "flex", padding: 10 }}>
            <Typography style={{ color: "red" }}>
              Problem Sending Email
            </Typography>
          </div>
        )}
      </StyledColDiv>

      <div style={{ display: "flex", justifyContent: "space-around" }}>
        <Button onClick={handleClose} variant="contained" color="inherit">
          Cancel
        </Button>
        {mutation.isLoading ? (
          <Button variant="contained" color="primary">
            <CircularProgress color="inherit" size={30} />
          </Button>
        ) : (
          <Button
            onClick={() => mutation.mutate()}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        )}
      </div>
    </StyledModalDiv>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      {body}
    </Modal>
  );
};

export default AdminOrderModal;
